import {useCallback} from 'react';

import {useController, useFormContext} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';

import {AddressFormItemType, AddressFormType, AddressType} from 'desktop/types/addressType';
import {useGetRegions} from 'hooks/location/location';

import {saveBillingAddress} from '../../store/reducers/deliveryStep/billingAddress/billingAddress';
import {saveShippingAddress} from '../../store/reducers/deliveryStep/shippingAddress/shippingAddress';
import {setAddressFieldValue} from '../../store/reducers/sagaStep/sagaStep';
import {getAddressesSameness} from '../../store/selectors/deliveryStep/deliveryStep';

/**
 * Save delivery field in redux for persisting
 */
export const useSaveDeliveryFieldInRedux = ({
    fieldKey,
    target,
}: {
    target: 'shippingAddress' | 'billingAddress';
    fieldKey: keyof AddressFormItemType;
}) => {
    const dispatch = useDispatch();
    const useThisAddressForBothAddresses = useSelector(getAddressesSameness);

    const {control, getValues, setValue} = useFormContext<AddressFormType>();
    const {
        fieldState: {error},
    } = useController<AddressFormType>({
        control,
        name: `${target}.${fieldKey}`,
    });
    const {regions: states} = useGetRegions(getValues()[target]?.country);

    const updateBillingIfSame = useCallback(
        (newAddressField: AddressType) => {
            const values = getValues();
            const value = values[target][fieldKey];

            if (useThisAddressForBothAddresses && target === 'shippingAddress') {
                setValue(`billingAddress.${fieldKey}`, String(value), {
                    shouldTouch: true,
                });
                dispatch(saveBillingAddress(newAddressField));
            }
        },
        [dispatch, fieldKey, getValues, setValue, target, useThisAddressForBothAddresses],
    );

    const addRegionIdForState = useCallback(
        (newAddressField: AddressType) => {
            const values = getValues();
            const value = values[target][fieldKey];

            if (fieldKey === 'state' && value) {
                const regionId: string =
                    states?.[values[target].country]?.find(s => s.label === value)?.id || '';

                if (regionId) {
                    newAddressField.regionID = {
                        error: false,
                        success: true,
                        value: regionId,
                    };
                }
            }
        },
        [fieldKey, getValues, states, target],
    );

    const saveValueInRedux = useCallback(() => {
        const values = getValues();
        const value = values[target][fieldKey]!;

        const newAddressField = {
            [fieldKey]: {
                error: Boolean(error?.message || ''),
                success: !error,
                value,
            },
        };

        addRegionIdForState(newAddressField);

        if (fieldKey === 'country') {
            dispatch(
                setAddressFieldValue({
                    fieldName: 'state',
                    target,
                    useThisAddressForBothAddresses,
                    value: '',
                }),
            );
            dispatch(
                setAddressFieldValue({
                    fieldName: 'regionID',
                    target,
                    useThisAddressForBothAddresses,
                    value: '',
                }),
            );
        }

        dispatch(
            setAddressFieldValue({
                fieldName: fieldKey,
                target,
                useThisAddressForBothAddresses,
                value,
            }),
        );

        if (target === 'shippingAddress') dispatch(saveShippingAddress(newAddressField));
        else dispatch(saveBillingAddress(newAddressField));

        updateBillingIfSame(newAddressField);
    }, [
        getValues,
        target,
        fieldKey,
        error,
        addRegionIdForState,
        dispatch,
        useThisAddressForBothAddresses,
        updateBillingIfSame,
    ]);

    return saveValueInRedux;
};
